import { EyeIcon, EyeOffIcon, ShipCircleIcon } from "../../shared/Icons";
import { isDarkModeAtom, themeAtom } from "../../shared/atoms";
import WithTooltip from "../../shared/components/WithTooltip";
import { TimeCharter } from "../models/search.interfaces";
import { ArrowLongRightIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { FC, useState } from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const InfoBadge = ({ title, value }: { title: string; value: React.ReactNode }) => {
  const theme = useAtomValue(themeAtom);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-medium leading-[14px] text-gray-400">{title}</span>
      <span
        className={twMerge("text-xs font-normal", theme.myVesselsTable.tailwind.textDefault)}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
};

const Divider: React.FC = () => {
  const isDark = useAtomValue(isDarkModeAtom);

  return <div className={twMerge("h-px w-full", isDark ? "bg-gray-600" : "bg-[#DFE4E2]")} />;
};

const VerticalDivider = () => <div className="w-px self-stretch bg-gray-300" />;

const CardTimeCharter: FC<{
  item: TimeCharter;
  getStatusColor: (status: string) => { border: string; text: string };
  truncateText: (text: string | undefined) => string;
  isInactiveTimeCharter?: boolean;
}> = ({ item, getStatusColor, truncateText, isInactiveTimeCharter = false }) => {
  const theme = useAtomValue(themeAtom);
  const voyagesNo = item.VOYAGE_NO.split(",");
  return (
    <div
      className={twMerge(
        "relative flex flex-col gap-2 rounded border py-4",
        isInactiveTimeCharter && theme.home.tailwind.backgound,
        theme.myVesselsTableRow.tableText.primary,
        theme.myVesselsTable.tailwind.tableBorder,
      )}
    >
      {/* CP Date , status and id section*/}
      <div className="flex w-full justify-between gap-4 px-4">
        {/* CP Date, and ID */}
        <div className="items-starts flex flex-col gap-1">
          <span className="text-xs font-bold">
            CP Date:{" "}
            {new Date(item.CP_DATE || "").toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
          <Link className="cursor-pointer self-start text-sm text-green" to={`/voyage/${item.TC_CODE}`}>
            {item.TC_CODE}
          </Link>
        </div>
        {/* Status */}
        <div
          className={`self-start rounded-e-full rounded-s-full border ${getStatusColor(item.TC_STATUS).border} px-2 py-1 text-center ${getStatusColor(item.TC_STATUS).text} text-xs font-medium`}
        >
          {item.TC_STATUS}
        </div>
      </div>
      <Divider />
      <div className={twMerge("relative mx-4 h-32")}>
        {item.VOYAGE_NO ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold">Voyages</span>
            <div className="flex flex-col gap-1">
              {voyagesNo.slice(0, 3).map((voyageNo, voyageIndex) => (
                <Link
                  key={voyageNo}
                  to={`/voyage/details/${item.TC_CODE}/${voyageNo}`}
                  className={twMerge(
                    "flex cursor-pointer items-center gap-1.5 py-1 hover:rounded",
                    theme.myVesselsTableRow.row.hover,
                  )}
                >
                  <span className="text-xs font-medium text-green">#{voyageNo}</span>
                  <div className="flex flex-1 gap-1.5">
                    <span className={twMerge("text-xs", theme.myVesselsTable.tailwind.textDefault)}>
                      {item.VOYAGE_STATUS_CODE?.split(",")[voyageIndex]}
                    </span>
                    <VerticalDivider />
                    <span className={twMerge("text-xs", theme.myVesselsTable.tailwind.textDefault)}>
                      {item.OPR_TYPE?.split(",")[voyageIndex]}
                    </span>
                    <VerticalDivider />
                    <span className={twMerge("text-xs", theme.myVesselsTable.tailwind.textDefault)}>
                      {item.OPS_COORDINATOR?.split(",")[voyageIndex]}
                    </span>
                  </div>
                  <ChevronRightIcon className="h-4 w-4" />
                </Link>
              ))}
            </div>
            {voyagesNo.length > 3 && (
              <Link className="cursor-pointer self-start text-xs font-medium text-green" to={`/voyage/${item.TC_CODE}`}>
                View all voyages
              </Link>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <ShipCircleIcon />
            <span className={twMerge("text-center text-xs", theme.myVesselsTable.tailwind.textDefault)}>
              There are still no scheduled voyages for this timecharter.
            </span>
          </div>
        )}
      </div>
      <Divider />
      <div className="flex w-full items-center justify-between gap-6 px-4">
        {/* TC Owner */}
        <InfoBadge
          title="TC Owner"
          value={
            <WithTooltip
              className={theme.myVesselsTable.tailwind.textDefault}
              id="tc-owner-tooltip"
              content={item?.TC_OWNER}
            >
              {truncateText(item?.TC_OWNER)}
            </WithTooltip>
          }
        />
        {/* Delivery */}
        <div className="flex items-center gap-2">
          <InfoBadge
            title="Delivery"
            value={new Date(item?.DELIVERY_DATE || "").toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          />
          <ArrowLongRightIcon className="h-4 w-4 self-end" />
          <InfoBadge
            title="Redelivery"
            value={new Date(item?.REDELIVERY_DT || "").toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          />
        </div>
      </div>
    </div>
  );
};

type ExpandedSearchRowProps = {
  timeCharters: TimeCharter[];
  getStatusColor: (status: string) => { border: string; text: string };
  truncateText: (text: string | undefined) => string;
};

export const ExpandedSearchRow: FC<ExpandedSearchRowProps> = ({ timeCharters, getStatusColor, truncateText }) => {
  const theme = useAtomValue(themeAtom);
  const [showClosed, setShowClosed] = useState(false);
  const inactiveStatuses = ["Closed", "Redelivered", "Canceled", "Withdrawn"];
  const [activeTimeCharters, inactiveTimeCharters] = [
    timeCharters.filter((item) => !inactiveStatuses.includes(item.TC_STATUS)),
    timeCharters.filter((item) => inactiveStatuses.includes(item.TC_STATUS)),
  ];
  const hasInactiveTimeCharters = inactiveTimeCharters.length > 0;
  return (
    <tr
      className={twMerge(
        "w-full border border-l-2 !border-l-green",
        theme.myVesselsTable.tailwind.tableBorder,
        theme.home.tailwind.panelBackground,
      )}
    >
      <td colSpan={10}>
        <div className="flex flex-col gap-6 px-6 pb-4 pt-2">
          <div className="flex flex-wrap gap-6">
            {activeTimeCharters.map((item, index) => (
              <CardTimeCharter key={index} item={item} getStatusColor={getStatusColor} truncateText={truncateText} />
            ))}
            {showClosed &&
              inactiveTimeCharters.map((item, index) => (
                <CardTimeCharter
                  key={index}
                  item={item}
                  getStatusColor={getStatusColor}
                  truncateText={truncateText}
                  isInactiveTimeCharter={true}
                />
              ))}
          </div>
          {hasInactiveTimeCharters && (
            <div className="flex justify-center leading-tight">
              <button
                className="flex items-center gap-2 text-green hover:bg-primary-10 focus:outline-none"
                onClick={() => setShowClosed(!showClosed)}
              >
                {showClosed ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                {showClosed ? "Hide" : "Show"} inactive timecharters
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};
